import { useState } from 'react';
import type {
  ElementDropTargetEventBasePayload,
  ElementDropTargetGetFeedbackArgs,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { createElementIdGenerator } from '../format/elements';
import type { ContainerElement, Element as FormElement } from '../format/form-definition';
import { useBuilder } from './context';
import type { Tile } from './fieldsPalette/tiles';
import { InsertionPosition, landingBeside, mayLand } from './elementInsertion';

type Dragged = { kind: 'form-builder-tile'; sample: FormElement };
type Side = 'before' | 'after' | 'inside';
type DropEvent = ElementDropTargetEventBasePayload;
type Placement = { side: Side; at: InsertionPosition | undefined };

const isTile = (data: Record<string, unknown>): data is Dragged => data.kind === 'form-builder-tile';

export const useTileDrag = (tile: Tile) => {
  const { form } = useBuilder();
  return () => ({
    kind: 'form-builder-tile',
    sample: tile.create(createElementIdGenerator(form)),
  });
};

const sideOf = (element: Element, clientY: number): 'before' | 'after' => {
  const box = element.getBoundingClientRect();
  return clientY < box.top + box.height / 2 ? 'before' : 'after';
};

const useInsertionDrop = (placement: (element: Element, clientY: number) => Placement) => {
  const builder = useBuilder();
  const [side, setSide] = useState<Side | null>(null);
  const over = ({ self, location }: DropEvent) => {
    setSide(
      location.current.dropTargets[0]?.element === self.element
        ? placement(self.element, location.current.input.clientY).side
        : null,
    );
  };
  return {
    side,
    params: {
      canDrop: ({ source, element, input }: ElementDropTargetGetFeedbackArgs) => {
        const { at } = placement(element, input.clientY);
        return isTile(source.data) && !!at && mayLand(builder.form, source.data.sample, at);
      },
      getDropEffect: () => 'copy' as const,
      onDragEnter: over,
      onDrag: over,
      onDropTargetChange: over,
      onDragLeave: () => setSide(null),
      onDrop: ({ source, self, location }: DropEvent) => {
        setSide(null);
        if (location.current.dropTargets[0]?.element !== self.element || !isTile(source.data)) return;
        const { at } = placement(self.element, location.current.input.clientY);
        if (!at || !mayLand(builder.form, source.data.sample, at)) return;
        builder.edit({ kind: 'addElement', element: source.data.sample, at });
        builder.select(source.data.sample.id);
      },
    },
  };
};

const FRAME_GAP = 8;
const LINE_WIDTH = 3;

export const useElementDrag = (element: FormElement) => {
  const { form } = useBuilder();
  const drop = useInsertionDrop((target, clientY) => {
    const side = sideOf(target, clientY);
    return { side, at: landingBeside(form, element.id, side) };
  });
  return {
    params: drop.params,
    line: drop.side === 'before' || drop.side === 'after' ? lineLook(drop.side) : {},
  };
};

const lineLook = (side: 'before' | 'after') => {
  const offset = -(FRAME_GAP + LINE_WIDTH) / 2;
  return {
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: side === 'before' ? offset : 'auto',
      bottom: side === 'after' ? offset : 'auto',
      height: LINE_WIDTH,
      borderRadius: `${LINE_WIDTH}px`,
      bgcolor: 'primary.main',
      zIndex: 3,
      pointerEvents: 'none',
    },
  } as const;
};

export const useEmptyPlaceDrop = (container: ContainerElement | null) => {
  const drop = useInsertionDrop(() => ({
    side: 'inside',
    at: { parentId: container?.id ?? null, index: 0 },
  }));
  return { params: drop.params, over: drop.side === 'inside' };
};
